// useMailBildAktionen.tsx — T-236 für gesicherte Mails (Akte, Mail im Vorgang):
// Klick auf ein Bild im Mailtext öffnet die Vorschau, Rechtsklick das eigene
// Menü mit „Vorschau" und „Speichern unter …".
//
// Bewusst schlanker als im Postfach-Fenster (MailDetailWindow): eine gesicherte
// Mail ist ein Archiv; „Beim Kontakt speichern" gibt es dort über die
// Anhangleiste, nicht am Bild.
//
// Der Viewer wird erst beim Klick nachgeladen: `DocumentViewerContent` bindet
// seinerseits `MailFileViewer` ein (Zirkelbezug) und zieht pdfjs mit.

import { ausgelagertesFenster } from "../windowManager/browserFenster/fensterRegister";
import * as React from "react";
import { useWindowManagerOptional } from "../windowManager/WindowManagerContext";
import { reportMailError } from "../../services/mailError";
import { zeigeGlobalenHinweis } from "../calendar/globalerHinweis";
import { BildMenue } from "./BildMenue";
import { bildDateiname, dataUriTyp, dataUriZuBlob } from "./inlineBilder";
import type { MailBild } from "./SandboxedMailBody";
import { speichereBlobUnter } from "./speichernUnter";

export type BildAnhangInfo = { name: string | null; contentType: string | null };

export function useMailBildAktionen(args: {
    /** Fenster-Schlüssel der Mail — derselbe Bild-Klick öffnet kein zweites Fenster. */
    mailKey: string;
    datum: Date | null;
    /** Anhang zum Bild (über `data-kmvs-cid`) — für einen sprechenden Dateinamen. */
    findeAnhang: (cid: string | null) => BildAnhangInfo | null;
}): { beiBild: ((b: MailBild) => void) | undefined; bildMenue: React.ReactNode } {
    const wm = useWindowManagerOptional();
    const [menue, setMenue] = React.useState<MailBild | null>(null);
    const schliessen = React.useCallback(() => setMenue(null), []);
    const { mailKey, datum, findeAnhang } = args;

    const name = (bild: MailBild) => {
        const anhang = findeAnhang(bild.cid);
        return bildDateiname(anhang?.name, anhang?.contentType ?? dataUriTyp(bild.dataUri), datum ?? new Date(NaN));
    };
    const speichern = async (bild: MailBild) => {
        try {
            await speichereBlobUnter(await dataUriZuBlob(bild.dataUri), name(bild));
        } catch (err) {
            zeigeGlobalenHinweis(
                reportMailError({
                    area: "mail",
                    action: "download-attachment",
                    error: err,
                    fallbackMessage: "Das Bild konnte nicht gespeichert werden.",
                }),
                { art: "fehler" },
            );
        }
    };
    const vorschau = async (bild: MailBild) => {
        if (!wm) return;
        const fileName = name(bild);
        const mimeType = findeAnhang(bild.cid)?.contentType ?? dataUriTyp(bild.dataUri) ?? "image/png";
        const { FileViewerContent } = await import("../documents/DocumentViewerContent");
        wm.openWindow({
            id: `file-viewer-mail-inline-${mailKey}-${bild.cid ?? `${bild.dataUri.length}-${bild.dataUri.slice(-16)}`}`,
            title: fileName,
            initialMaximized: true,
            browserFensterNeben: ausgelagertesFenster(),
            render: () => (
                <FileViewerContent
                    // Das Bild steht schon im Mailtext — kein zweiter Abruf.
                    load={() => dataUriZuBlob(bild.dataUri)}
                    fileName={fileName}
                    mimeType={mimeType}
                    onDownload={() => void speichern(bild)}
                />
            ),
        });
    };

    const beiBild = (bild: MailBild) => {
        if (bild.kontextmenue) setMenue(bild);
        else void vorschau(bild);
    };

    return {
        // Ohne Fenster-Verwaltung (z. B. eingebettet ohne Shell) bleibt das Bild,
        // was es war — dann übernimmt auch das Browsermenü.
        beiBild: wm ? beiBild : undefined,
        bildMenue: menue ? (
            <BildMenue
                position={{ x: menue.x, y: menue.y }}
                onVorschau={() => void vorschau(menue)}
                onSpeichernUnter={() => void speichern(menue)}
                onSchliessen={schliessen}
            />
        ) : null,
    };
}
